import React, { useState } from 'react';
import { Flag, MapPin, Link2, Building2 } from 'lucide-react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { submitUserReport } from 'wasp/client/operations';
import { cn } from '../../lib/utils';
import { useLocationContext } from '../contexts/LocationContext';
import { useOrgNavMode } from '../contexts/OrgNavModeContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';

type ReportType = 'generic_feedback' | 'report_location' | 'report_post' | 'report_section';

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  generic_feedback: 'General Feedback',
  report_location: 'Report this Location',
  report_post: 'Report this Post',
  report_section: 'Report Section as Not Applicable',
};

const REASONS_BY_TYPE: Record<ReportType, string[]> = {
  generic_feedback: ['Suggestion', 'Complaint', 'Compliment', 'Bug / Something broken', 'Other'],
  report_location: ['Incorrect information', 'No longer exists', 'Duplicate location', 'Other'],
  report_post: ['Spam', 'Inappropriate content', 'Misinformation', 'Other'],
  report_section: ['Not applicable to this organization', 'Outdated', 'Incorrect', 'Other'],
};

interface ReportButtonProps {
  /** Optional extra context to append (e.g. from a specific post or org page) */
  contextInfo?: string;
  defaultReportType?: ReportType;
}

function buildContextString(opts: {
  location?: string;
  orgType?: string;
  pageUrl: string;
  extra?: string;
}): string {
  const parts: string[] = [];
  if (opts.location) parts.push(`Location: ${opts.location}`);
  if (opts.orgType) parts.push(`Org Type: ${opts.orgType}`);
  parts.push(`Page: ${opts.pageUrl}`);
  if (opts.extra) parts.push(`Extra: ${opts.extra}`);
  return parts.join(' | ');
}

export default function ReportButton({ contextInfo, defaultReportType = 'generic_feedback' }: ReportButtonProps) {
  const [open, setOpen] = useState(false);
  const [reportType, setReportType] = useState<ReportType>(defaultReportType);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { selectedLocation } = useLocationContext();
  const { orgTypeKey } = useOrgNavMode();
  const routerLocation = useRouterLocation();

  const pageUrl = window.location.origin + routerLocation.pathname;
  const locationLabel = selectedLocation
    ? `${selectedLocation.name} (${selectedLocation.locationCode})`
    : undefined;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Reset form on open
      setReportType(defaultReportType);
      setReason('');
      setDetails('');
      setSubmitted(false);
    }
    setOpen(next);
  };

  const handleReportTypeChange = (type: ReportType) => {
    setReportType(type);
    setReason('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitUserReport({
        reportType,
        reason: reason || undefined,
        details: details || undefined,
        contextInfo: buildContextString({
          location: locationLabel,
          orgType: orgTypeKey ?? undefined,
          pageUrl,
          extra: contextInfo,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit report:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button
          className="relative p-1.5 hover:bg-sidebar-hover rounded-lg transition-colors"
          title="Report an issue or give feedback"
        >
          <Flag className="w-5 h-5 text-header-text-muted" />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-destructive" />
            <SheetTitle className="text-base">Report / Feedback</SheetTitle>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-16">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-foreground">Thank you for your feedback!</p>
              <p className="text-xs text-muted-foreground">We'll review your report shortly.</p>
              <button
                onClick={() => setOpen(false)}
                className="mt-2 px-5 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Let us know if something is broken or share your thoughts.
              </p>

              {/* Auto-detected context */}
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Submitting from
                </p>
                {locationLabel && (
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span>{locationLabel}</span>
                  </div>
                )}
                {orgTypeKey && (
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span>{orgTypeKey}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-foreground min-w-0">
                  <Link2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="truncate" title={pageUrl}>{routerLocation.pathname}</span>
                </div>
                {contextInfo && (
                  <div className="flex items-start gap-2 text-xs text-foreground">
                    <span className="font-medium flex-shrink-0">Note:</span>
                    <span>{contextInfo}</span>
                  </div>
                )}
              </div>

              {/* Report Type */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">
                  What would you like to do?
                </label>
                <select
                  value={reportType}
                  onChange={(e) => handleReportTypeChange(e.target.value as ReportType)}
                  className="w-full text-sm border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {(Object.keys(REPORT_TYPE_LABELS) as ReportType[]).map((type) => (
                    <option key={type} value={type}>
                      {REPORT_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">
                  Reason
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full text-sm border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a reason</option>
                  {REASONS_BY_TYPE[reportType].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Details */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">
                  Additional Details
                </label>
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Please provide more context..."
                  rows={5}
                  className="w-full text-sm border border-input rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer actions */}
        {!submitted && (
          <div className="px-6 py-4 border-t border-border flex-shrink-0 flex gap-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 px-4 py-2 text-sm font-medium text-foreground border border-input rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form=""
              disabled={isSubmitting}
              onClick={handleSubmit as any}
              className={cn(
                "flex-1 px-4 py-2 text-sm font-medium text-primary-foreground rounded-lg transition-colors",
                isSubmitting ? "bg-primary/60 cursor-not-allowed" : "bg-primary hover:bg-primary/90"
              )}
            >
              {isSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}



